import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import css from './Pagination.module.scss';
import Button from '../Button/Button';
import ChevronLeft from '../Icons/ChevronLeft';
import ChevronRight from '../Icons/ChevronRight';

const Pagination = ({
  itemsPerPage,
  totalItems,
  currentPage,
  onPageChange,
}) => {
  const lastPageNumber = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const [draftPage, setDraftPage] = useState(String(currentPage));

  useEffect(() => {
    setDraftPage(String(currentPage));
  }, [currentPage]);

  if (totalItems <= itemsPerPage) {
    return null;
  }

  const rangeStart = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(currentPage * itemsPerPage, totalItems);

  const goToPage = (target) => {
    const clamped = Math.min(lastPageNumber, Math.max(1, target));
    setDraftPage(String(clamped));
    if (clamped !== currentPage) {
      onPageChange(clamped);
    }
  };

  const handlePreviousPage = () => {
    goToPage(currentPage - 1);
  };

  const handleNextPage = () => {
    goToPage(currentPage + 1);
  };

  const handleJumpInputChange = (e) => {
    setDraftPage(e.target.value);
  };

  const handleJumpCommit = () => {
    const parsed = parseInt(draftPage, 10);
    if (Number.isFinite(parsed)) {
      goToPage(parsed);
    } else {
      setDraftPage(String(currentPage));
    }
  };

  const handleJumpKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleJumpCommit();
    }
  };

  return (
    <div className={css.container}>
      <span className={css.range}>{rangeStart}–{rangeEnd} of {totalItems}</span>
      <div className={css.controls}>
        <Button
          type="button"
          variant="tertiary"
          size="small"
          icon={<ChevronLeft />}
          onClick={handlePreviousPage}
          disabled={currentPage <= 1}
        >
          Previous
        </Button>
        <span className={css.jumpRow}>
          <span className={css.jumpLabel}>Page</span>
          <input
            type="number"
            min={1}
            max={lastPageNumber}
            className={css.jumpInput}
            value={draftPage}
            onChange={handleJumpInputChange}
            onBlur={handleJumpCommit}
            onKeyDown={handleJumpKeyDown}
          />
          <span className={css.jumpLabel}>of {lastPageNumber}</span>
        </span>
        <Button
          type="button"
          variant="tertiary"
          size="small"
          icon={<ChevronRight />}
          iconPosition="right"
          onClick={handleNextPage}
          disabled={currentPage >= lastPageNumber}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

Pagination.propTypes = {
  itemsPerPage: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

export default Pagination;
